import React, { useState, type JSX } from 'react';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import { Dossier } from '../../../amplify/backend/function/data/lib/dat/services/dat.types';
import { Modal } from '../Modal';

type Props = {
  readonly open: boolean;
  readonly fahrzeugKandidaten: string[];
  readonly onClose: () => void;
  readonly onSelect: (fahrzeug: string) => void;
};

export function FahrzeugAuswahlDialog({ open, fahrzeugKandidaten, onClose, onSelect }: Props): JSX.Element {
  const [selectedFahrzeug, setSelectedFahrzeug] = useState<string | null>(null);

  const handleFahrzeugSelect = (fahrzeug: string) => {
    const dossier = JSON.parse(fahrzeug) as Dossier;
    const primaryKandidat = fahrzeugKandidaten.map((f) => JSON.parse(f) as Dossier).find((d) => d.TokenOfVinResult);

    if (!dossier.TokenOfVinResult && primaryKandidat?.TokenOfVinResult) {
      dossier.TokenOfVinResult = primaryKandidat.TokenOfVinResult;
    }

    const selectedVINResult = dossier.Vehicle?.VINResult as any;
    const primaryVINResult = primaryKandidat?.Vehicle?.VINResult as any;
    if (!selectedVINResult?.VINECodes && primaryVINResult?.VINECodes) {
      dossier.Vehicle!.VINResult = { ...selectedVINResult, VINECodes: primaryVINResult.VINECodes };
    }

    const kbaNumbers: string[] = (dossier.Vehicle as any)?.KbaNumbersN?.KbaNumber ?? [];
    if (kbaNumbers.length <= 1) {
      onSelect(JSON.stringify(dossier));
    } else {
      setSelectedFahrzeug(JSON.stringify(dossier));
    }
  };

  const handleKbaSelect = (kbaNumber: string) => {
    if (!selectedFahrzeug) return;
    const dossier = JSON.parse(selectedFahrzeug) as Dossier;
    (dossier.Vehicle as any).KbaNumbersN = { KbaNumber: [kbaNumber] };
    onSelect(JSON.stringify(dossier));
  };

  if (selectedFahrzeug) {
    const dossier = JSON.parse(selectedFahrzeug) as Dossier;
    const kbaNumbers: string[] = (dossier.Vehicle as any)?.KbaNumbersN?.KbaNumber ?? [];
    return (
      <Modal
        title="KBA-Nummer auswählen"
        openModal={open}
        setOpenModal={() => onClose()}
        body={
          <List>
            <ListItemButton onClick={() => setSelectedFahrzeug(null)}>← Zurück</ListItemButton>
            {kbaNumbers.map((kba) => (
              <ListItemButton key={kba} onClick={() => handleKbaSelect(kba)}>
                {kba}
              </ListItemButton>
            ))}
          </List>
        }
      />
    );
  }

  return (
    <Modal
      title="Fahrzeugauswahl"
      openModal={open}
      setOpenModal={() => onClose()}
      body={
        <List>
          {fahrzeugKandidaten.map((fahrzeug) => {
            const dossier = JSON.parse(fahrzeug) as Dossier;
            const key = dossier.Vehicle?.DatECode ?? fahrzeug;
            return (
              <ListItemButton key={key} onClick={() => handleFahrzeugSelect(fahrzeug)} sx={{ flexDirection: 'column', alignItems: 'start' }}>
                <VehicleInformation vehicleString={fahrzeug} />
              </ListItemButton>
            );
          })}
        </List>
      }
    />
  );
}

function VehicleInformation({ vehicleString }: { readonly vehicleString: string }): JSX.Element {
  const dossier = JSON.parse(vehicleString) as Dossier;
  const vehicle = dossier.Vehicle;

  return (
    <>
      <span>
        {vehicle?.ManufacturerName?.$value} {vehicle?.BaseModelName?.$value} {vehicle?.SubModelName?.$value ?? vehicle?.DatECode}
      </span>
      <small>{vehicle?.DATECodeEquipment?.EquipmentPosition.find((position) => position.Description.toLowerCase().includes('motor'))?.Description}</small>
    </>
  );
}
